import {api, events} from '@bitstillery/common/app'
import type {PortalMenuCategory} from '@bitstillery/common/api/fact2server'
import {copy_object} from '@bitstillery/common/lib/utils'

import {$s} from '@/app'

export interface PublicSearchCounts {
    brand_holder: number
    category: number
}

export async function get_search_counts(): Promise<PublicSearchCounts | null> {
    const {result, success} = await api.get('portal/search-counts')
    if (!success) {
        return null
    }
    return result
}

interface SuggestionCommon {
    count: number | null
    name: string
    offer_item_type?: 'tbo' | 'stock' | undefined
}

/** A category suggestion; its value is the category's artkey. */
export interface CategorySuggestion extends SuggestionCommon {
    children: CategorySuggestion[] | null
    parent: CategorySuggestion | null
    type: 'category'
    value: number
}

/** A product name or brand holder suggestion, matched by its text. */
export interface TermSuggestion extends SuggestionCommon {
    children: null
    parent: null
    type: 'search' | 'brand_holder'
    value: string
}

export type SuggestionType = CategorySuggestion | TermSuggestion

/**
 * The public variants of brand-holders and product-names carry an extra `public/` path segment
 * because their authenticated counterparts already occupy `portal/brand-holders` and
 * `portal/product-names`. The other public endpoints sit directly under `portal/`.
 */
export async function get_brand_holders(use_public = false): Promise<SuggestionType[]> {
    const {result, success} = use_public ? await api.get('portal/public/brand-holders') : await api.get('portal/brand-holders')
    if (!success) {
        return []
    }

    return result.map((brand_holder: {artkey: number; name: string; number_of_products?: number}): SuggestionType => ({
        children: null,
        count: brand_holder.number_of_products ?? null,
        name: brand_holder.name.toLowerCase(),
        parent: null,
        type: 'brand_holder',
        value: `${brand_holder.artkey}`,
    }))
}

/**
 * A null `offer_item_type` asks for every product name in one list. That is what a visitor without
 * a login gets: stock versus TBO says whether we hold an article or source it on demand, which is
 * commercial information, not a search facet — so the suggestions carry no availability either.
 */
export async function get_product_names(offer_item_type: 'stock' | 'tbo' | null, use_public = false): Promise<SuggestionType[]> {
    const query = offer_item_type ? (`?offer_item_type=${offer_item_type}` as const) : ''
    const {result: result_product_names, success} = use_public
        ? await api.get(`portal/public/product-names${query}`)
        : await api.get(`portal/product-names${query}`)
    if (!success) {
        return []
    }

    return result_product_names.map((_name) => {
        const name = _name.toLowerCase()
        return {
            children: null,
            count: null,
            name: name,
            offer_item_type: offer_item_type ?? undefined,
            parent: null,
            type: 'search',
            value: name,
        }
    })
}

export async function get_search_categories(use_public = false): Promise<SuggestionType[]> {
    // TODO: implement a product group landing page.
    const product_group_artkey = 1 // Spirits, the only product group for now

    const {result, success} = use_public
        ? await api.get('portal/categories')
        : await api.get(`portal/groups/${product_group_artkey}/categories`)
    if (!success) {
        return []
    }
    // portal/categories answers with a bare array, which has no categories key.
    const menu: PortalMenuCategory[] = 'categories' in result ? result.categories : []
    const lookup: Record<string, string> = {}
    for (const parent_category of menu) {
        for (const child_category of parent_category.children) {
            lookup[String(child_category.artkey)] = child_category.name
        }
        lookup[String(parent_category.artkey)] = parent_category.name
    }

    $s.categories.lookup = lookup
    // A deep copy of the categories, which only the group endpoint's CategoriesResponse has.
    $s.categories.$menu.value = copy_object(menu)

    const category_suggestions: SuggestionType[] = []
    const child_suggestions: SuggestionType[] = []
    for (const parent_category of menu) {
        const parent_count = parent_category.number_of_products
        const parent_suggestion: CategorySuggestion = {
            children: null,
            count: parent_count > 0 ? parent_count : null,
            name: parent_category.name.toLowerCase(),
            parent: null,
            type: 'category',
            value: parent_category.artkey,
        }

        const parents_children: CategorySuggestion[] = []
        for (const child_category of parent_category.children) {
            const child_count = child_category.number_of_products
            if (child_count === 0 && !use_public) {
                continue
            }

            const child_suggestion: CategorySuggestion = {
                children: null,
                count: child_count > 0 ? child_count : null,
                name: child_category.name.toLowerCase(),
                parent: parent_suggestion,
                type: 'category',
                value: child_category.artkey,
            }
            child_suggestions.push(child_suggestion)
            parents_children.push(child_suggestion)
        }

        if (parents_children.length > 0) {
            parent_suggestion.children = parents_children
        }

        if (parent_count > 0 || use_public) {
            category_suggestions.push(parent_suggestion)
        }
    }
    return category_suggestions.concat(child_suggestions)
}

export function init() {
    events.on('identity.login', () => {
        events.emit('search.match-suggestions')
    })
}
